<template lang='pug'>
.permit-slide-out(:class='{ "has-permit" : asset !== null }')
	asset-slide-out(
		:state='state'
		:asset='asset'
		:asset-key='assetKey'
		:asset-name='assetName'
		@close='reset'
		@edit='$emit( "edit" )'
		@delete='$emit( "delete" )'
	)

		.title-container(
			slot='title'
		)
			h1.title(v-if='asset') Permit Reference
			h1.loading-indicator.title(v-else) Loading
				span .
				span .
				span .

		page.permits-page.side-sheet-content(
			slot='side-sheet-content'
			:tabs='tabs'
		)

			.body-wrapper(slot='main-body' v-if='asset')
				.overlay(:class='{ "show" : showOverlay }')
					.box
						.top
							.icon-close(@click='showOverlay = false')
							p You have successfully created Permit \#{{ asset.number }}. Would you like to download the associated PDF now?
						.button-wrapper.separate-gradient.down
							.button(@click='downloadPdf') Download

				permit(
					v-if='asset'
					:permit='asset'
				)

				.loading-indicator.spacey(v-else) Loading
					span .
					span .
					span .

			.body(slot='tab-comments' v-if='asset')
				comments(
					:section-id='`permits-${asset.permitsKey}`'
					:user='user'
					@replied-to-comment='email'
				)

			.body.documents.separate-gradient.down(slot='tab-documents' v-if='asset')
				document-list(
					:documents='asset.folder'
					:a-files='asset.file'
					:assetKey='asset.permitsKey'
					@close='$emit( "close" )'
				)

			.body.documents.separate-gradient.down(slot='tab-forms' v-if='asset')
				.document(@click='downloadPdf')
					p FID {{ asset.permitsType.description }} \#{{asset.number}}
					.download-button.separate-gradient.right
						.icon
							.icon-download
						p(v-if='downloadState === "idle"') Download

						p.loading-indicator(v-else-if='downloadState === "loading"') Downloading
							span .
							span .
							span .

						p(v-else-if='downloadState === "downloaded"') Downloaded

						p(v-else-if='downloadState === "failed"') Download Failed
</template>

<script>
import AssetSlideOut from '@/components/slide-outs/AssetSlideOut';
import Permit from '@/components/slide-outs/permits/Permit';
import SideSheet from '@/components/SideSheet';
import Page from '@/components/Page';
import Comments from '@/components/comments';
import DropBox from '@/components/form-inputs/DropBox';
import DocumentList from '@/components/DocumentList';

import { objectOrNull } from '@/lib/vue-validators';

import { SendEmail, DownloadPermitsTemplate } from '@/lib/API';

export default {
	name : 'permit-slide-out',

	props : {
		state : {
			type     : String,
			required : true,
		},

		asset : {
			required  : true,
			validator : objectOrNull,
		},

		assetKey : {
			required : true,
		},

		assetName : {
			required : true,
		},

		isNewPermit : {
			type    : Boolean,
			default : false,
		}
	},

	data : () => ( {

		tabs : {
			'comments'  : 'Comments',
			'documents' : 'Documents',
			'forms'     : 'Forms',
		},

		showOverlay   : false,
		downloadState : 'idle',

	} ),

	watch : {

		isNewPermit : {
			immediate : true,
			handler( val ) {

				if ( val ) {
					this.showOverlay = val;
				}

			}
		},

	},

	computed : {

		user() {
			return this.$store.state.postgresUser;
		}

	},

	methods : {

		email( recipientKey, link ) {
			const { asset } = this;

			const sectionDescription = `Permit #${asset.number}`;

			const emailData = {
				sectionDescription,
				link
			};

			SendEmail( this.user.usersKey, [recipientKey], 'reply', emailData );
		},

		async downloadPdf() {
			this.downloadState = 'loading';

			const stateIndicator = await this.$store.dispatch( 'displayState', {
				message : 'Downloading PDF...',
				state   : 'loading'
			} );

			DownloadPermitsTemplate( this.assetKey )
				.then( () => {
					stateIndicator.resolve( 'Downloaded PDF' );

					// console.log( 'Downloading template' );
					this.downloadState = 'downloaded';

					window.setTimeout( () => {
						this.downloadState = 'idle';
					}, 4000 );
				} )
				.catch( ( err ) => {
					stateIndicator.fail( { header : 'Didn\'t Download PDF', message : '' } );

					console.warn( err );

					this.downloadState = 'failed';
				} );
		},

		reset() {
			this.$emit( 'close' );
			this.downloadState = 'idle';
		}

	},

	components : {
		AssetSlideOut,
		Permit,
		SideSheet,
		Page,
		Comments,
		DropBox,
		DocumentList
	}
};
</script>

<style lang='scss'>
.permit-slide-out {

	&:not(.has-permit) {

		.toggle,
		.main {
			opacity: 0;
		}
	}

	.body-wrapper {
		width: 100%;

		.loading-indicator {
			text-align: center;
		}
	}

	.page {

		.main-body {
			position: unset;
		}

		.overlay {
			position: absolute;
			top: 0;
			left: 0;
			width: 100%;
			height: 100%;
			background: rgba( $color-primary, .7 );
			z-index: 10;
			transition: opacity 0.2s ease;
			opacity: 1;

			&:not(.show) {
				opacity: 0;
				pointer-events: none;

				.box {
					transform: translate(-50%,-30px);
				}
			}

			.box {
				position: absolute;
				top: calc(100%/3);
				left: 50%;
				transform: translateX(-50%);
				width: 50%;
				transition: transform 0.2s ease;

				.top {
					padding: 30px;
					position: relative;

					.icon-close {
						position: absolute;
						top: 15px;
						right: 15px;
					}

					p {
						text-align: center;
						color: $blueGrey;
						margin-top: 10px;
						line-height: 175%;
					}
				}

				.button-wrapper {
					width:100%;
					position: relative;
					height: 60px;
					margin-top: 10px;

					.button {
						position: absolute;
						left: 50%;
						top: 0;
						transform: translate(-50%,-50%);

						&:not(:hover) {
							box-shadow: $secondaryBoxShadow;
						}
					}
				}
			}
		}

		.toggle {
			margin-top: 30px;
			margin-left: 0px;
		}

		.main {

			.body {

				> p.spacey {
					text-align: center;
				}

				.comment-section {

					.comment-count {
						padding: 15px 35px;
					}

					.comment-thread-container {
						padding: 15px 35px;
					}
				}
			}
		}

		.documents {
			padding: 15px;

			.document {
				display: flex;
				align-items: center;
				background: white;
				margin-bottom: 15px;
				border-radius: 10px;
				box-shadow: $secondaryBoxShadow;
				cursor: pointer;
				transform: scale(1,1);
				transition: transform 0.2s ease;

				&:active, &:focus {
					transform: scale(.95,.95) !important;
				}

				p {
					flex: 1 1 0;
				}

				> p,
				.download-button {
					padding: 10px;
				}

				.download-button {
					display: flex;
					align-items: center;
					align-self: stretch;

					.icon {
						height: 30px;
						width: 30px;
						border-radius: 50%;
						margin-right: 10px;
						border: 2px solid $blueGrey;
						position: relative;

						.icon-download {
							position: absolute;
							top: 50%;
							left: 50%;
							transform: translate(-50%,-55%);

							&::before {
								color: $blueGrey;
								font-size: 18px;
							}
						}
					}

					p {
						color: $blueGrey;
					}
				}
			}
		}
	}
}

@media only screen and ( max-width: 640px ) {

	.permit-slide-out {

		.permit {

			.info {

				.list-layout {

					.section {

						.list-item {
							flex: 0 0 100%;

							&.item-issuedBy,
							&.item-permittee {
								flex: 0 0 50%;
							}
						}
					}
				}
			}
		}

		.header {
			padding-bottom: 0 !important;
		}

		.full-page .page {
			margin-top: 30px;

			.toggle {
				display: none !important;
			}
		}
	}
}
</style>
